import { useEffect, useMemo, useRef, useState } from 'react';
import { ACHIEVEMENT_GROUPS, getAchievementState, getAchievementStats, syncAchievementsFromServer } from '../../data/achievements';
import { getPlayerHiddenPointsFound, getPlayerHiddenPointsResetMs, HIDDEN_POINTS_STORAGE_KEY, HIDDEN_POINTS_TOTAL, PLAYER_GAMES_STORAGE_KEY } from '../../data/hiddenPoints';
import { fetchMyHiddenPoints } from '../../infrastructure/hiddenPointsApi';
import nacanacaLogo from '../../../../assets/logo.webp';
import hiddenPointIcon from '../../../../assets/puntos1.webp';
import MapAmbientAudio from './MapAmbientAudio';
import MapInstructions from './MapInstructions';

// Cuenta regresiva HH:MM:SS con segundero, para la regeneración de la hora.
const formatCountdown = (ms) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(Math.floor(totalSec / 3600))}:${pad(Math.floor((totalSec % 3600) / 60))}:${pad(totalSec % 60)}`;
};

const formatProgress = (achievement) => achievement.id === 'pulso-de-acero'
  ? `${Math.floor(achievement.current)}s / ${achievement.goal}s`
  : `${achievement.current} / ${achievement.goal}`;

export default function AchievementsBar({ playerName, mapMode = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [achievements, setAchievements] = useState(() => getAchievementState());
  const [globalPoints, setGlobalPoints] = useState(() => getAchievementStats().totalScore);
  const [hiddenFound, setHiddenFound] = useState(() => getPlayerHiddenPointsFound(playerName));
  const [hiddenResetMs, setHiddenResetMs] = useState(() => getPlayerHiddenPointsResetMs(playerName));
  // Contador oficial del servidor (el único que suma al puntaje). El valor
  // local queda solo como respaldo visual sin sesión.
  const [officialHidden, setOfficialHidden] = useState(null);
  // Fin de la hora oficial (regeneración) + reloj local para la reversa.
  const [regenEndsAt, setRegenEndsAt] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const lastZeroRefetch = useRef(0);

  useEffect(() => {
    if (officialHidden && Number.isFinite(officialHidden.nextResetMs)) {
      setRegenEndsAt(Date.now() + Math.max(0, officialHidden.nextResetMs));
    }
  }, [officialHidden]);

  useEffect(() => {
    if (!regenEndsAt) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [regenEndsAt]);

  const countdownMs = Math.max(0, regenEndsAt - nowMs);
  const countdownLabel = regenEndsAt ? formatCountdown(countdownMs) : null;

  // Al llegar a cero la hora cambió y todo se regeneró: se refresca el
  // contador oficial (una vez por frontera, con guardia anti-bucle).
  const countdownIsZero = regenEndsAt > 0 && countdownMs === 0;
  useEffect(() => {
    if (!countdownIsZero || Date.now() - lastZeroRefetch.current < 30000) return;
    lastZeroRefetch.current = Date.now();
    fetchMyHiddenPoints().then(setOfficialHidden).catch(() => {});
  }, [countdownIsZero]);

  // En el mapa se sondea el estado oficial cada minuto para que la reversa
  // y el contador sigan exactos aunque cambie la hora con la app abierta.
  useEffect(() => {
    if (!mapMode) return undefined;
    const id = window.setInterval(() => {
      fetchMyHiddenPoints().then(setOfficialHidden).catch(() => {});
    }, 60000);
    return () => window.clearInterval(id);
  }, [mapMode, playerName]);

  useEffect(() => {
    let cancelled = false;
    setOfficialHidden(null);
    fetchMyHiddenPoints()
      .then((status) => { if (!cancelled) setOfficialHidden(status); })
      .catch(() => { if (!cancelled) setOfficialHidden(null); });
    return () => { cancelled = true; };
  }, [playerName]);

  useEffect(() => {
    const refresh = () => {
      setAchievements(getAchievementState());
      setGlobalPoints(getAchievementStats().totalScore);
    };
    window.addEventListener('nacanaca-achievements-updated', refresh);
    return () => window.removeEventListener('nacanaca-achievements-updated', refresh);
  }, []);

  // Progreso oficial por cuenta; ante una caída solo se conserva la última
  // respuesta autenticada del mismo jugador.
  useEffect(() => {
    let cancelled = false;
    setAchievements(getAchievementState());
    setGlobalPoints(getAchievementStats().totalScore);
    const sync = () => {
      syncAchievementsFromServer().catch(() => {
        if (!cancelled) setAchievements(getAchievementState());
      });
    };
    sync();
    window.addEventListener('nacanaca-achievements-sync', sync);
    return () => {
      cancelled = true;
      window.removeEventListener('nacanaca-achievements-sync', sync);
    };
  }, [playerName]);

  // Puntos ocultos: el iframe acredita en servidor y avisa por postMessage.
  // Ante un hallazgo oficial se refresca el contador del servidor y el total
  // (el puntaje ya incluye los puntos ocultos verificados).
  useEffect(() => {
    const refreshHidden = () => {
      setHiddenFound(getPlayerHiddenPointsFound(playerName));
      setHiddenResetMs(getPlayerHiddenPointsResetMs(playerName));
    };
    const refreshOfficial = () => {
      fetchMyHiddenPoints()
        .then(setOfficialHidden)
        .catch(() => {});
      // El total oficial (partidas + ocultos) vive en /api/achievements.
      window.dispatchEvent(new CustomEvent('nacanaca-achievements-sync'));
    };
    const handleStorage = (event) => {
      if (!event.key || event.key === HIDDEN_POINTS_STORAGE_KEY || event.key === PLAYER_GAMES_STORAGE_KEY) refreshHidden();
    };
    const handleMessage = (event) => {
      if (event.origin === window.location.origin && event.data?.type === 'nacanaca-hidden-points-updated') {
        refreshHidden();
        if (event.data?.official) refreshOfficial();
      }
    };
    refreshHidden();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('message', handleMessage);
    };
  }, [playerName]);

  const displayHiddenFound = officialHidden && Number.isFinite(officialHidden.foundCount)
    ? officialHidden.foundCount
    : hiddenFound;
  const displayHiddenResetMs = officialHidden && Number.isFinite(officialHidden.nextResetMs)
    ? officialHidden.nextResetMs
    : hiddenResetMs;

  const unlocked = useMemo(() => achievements.filter((achievement) => achievement.unlocked), [achievements]);
  const groupedAchievements = useMemo(() => {
    const order = ['solo', 'duelos', 'exploracion'];
    return order
      .map((group) => ({
        group,
        title: ACHIEVEMENT_GROUPS[group] || group,
        items: achievements.filter((achievement) => (achievement.group || 'exploracion') === group),
      }))
      .filter((section) => section.items.length > 0);
  }, [achievements]);
  const hiddenResetMinutes = Math.max(1, Math.ceil(displayHiddenResetMs / 60000));

  useEffect(() => {
    if (!isOpen) return undefined;
    setHiddenFound(getPlayerHiddenPointsFound(playerName));
    setHiddenResetMs(getPlayerHiddenPointsResetMs(playerName));
    fetchMyHiddenPoints().then(setOfficialHidden).catch(() => {});
    const id = window.setInterval(() => {
      setHiddenFound(getPlayerHiddenPointsFound(playerName));
      setHiddenResetMs(getPlayerHiddenPointsResetMs(playerName));
      fetchMyHiddenPoints().then(setOfficialHidden).catch(() => {});
    }, 60000);
    return () => window.clearInterval(id);
  }, [isOpen, playerName]);

  return (
    <>
      <div className={`pointer-events-auto fixed inset-x-0 top-0 z-[90] items-center gap-2 border-b border-black bg-black px-3 py-2 backdrop-blur-md md:px-6 ${mapMode ? 'flex flex-col justify-center sm:flex-row' : 'flex justify-center'}`}>
        {mapMode && <div className="relative flex min-h-8 w-full min-w-0 items-center justify-center sm:min-h-0 sm:w-auto sm:justify-start sm:gap-3 sm:border-r sm:border-white/15 sm:pr-3">
          <img src={nacanacaLogo} alt="Ñaca Ñaca" className="absolute left-0 h-8 w-8 object-contain sm:static sm:h-10 sm:w-10 sm:shrink-0" />
          <div className="min-w-0 px-10 text-center sm:px-0 sm:text-left">
            <p className="whitespace-nowrap font-display text-[11px] uppercase leading-none text-[#C084FC] min-[380px]:text-sm sm:truncate sm:text-base">Mapa de la maldición</p>

          </div>
          <div className="absolute inset-y-0 right-0 flex items-center">
            <MapInstructions buttonClassName="flex sm:hidden" />
          </div>
        </div>}
        <div className={mapMode ? 'flex w-full min-w-0 items-center justify-center gap-2 border-t border-white/10 pt-2 sm:contents' : 'contents'}>
          <div className="flex min-w-0 items-center gap-2">
            <span className="max-w-[90px] truncate text-[10px] font-black uppercase tracking-[.14em] text-[#C084FC] sm:max-w-[100px] md:max-w-none">{playerName}</span>
            {mapMode && <span className="flex shrink-0 items-center gap-1 text-[9px] font-black uppercase tabular-nums text-[#F7E04B]" title="Puntos oficiales de partidas verificadas">
              Puntos: {globalPoints.toLocaleString()}
            </span>}

          </div>
          <button type="button" onClick={() => setIsOpen(true)} className="shrink-0 border border-[#A78BFA] bg-[#7C3AED] px-3 py-2 text-[9px] font-black uppercase tracking-[.12em] text-white shadow-[3px_3px_0_rgba(0,0,0,.45)] active:translate-y-px" aria-label="Ver todos los logros">
            Logros <span className="text-white">{unlocked.length}/{achievements.length}</span>
          </button>
          {mapMode && (
            <span className="flex shrink-0 items-center gap-1">
              <MapAmbientAudio />
              <MapInstructions buttonClassName="hidden sm:flex" />
            </span>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="pointer-events-auto absolute inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/85 px-4 pb-8 pt-20 backdrop-blur-sm md:pt-24" role="dialog" aria-modal="true" aria-label="Medallas y logros">
          <div className="w-full max-w-xl border border-white/15 bg-black p-5 shadow-[8px_10px_0_rgba(0,0,0,.55)] md:p-7">
            <div className="flex items-start justify-between gap-4">
              <div><p className="text-[10px] font-black uppercase tracking-[.22em] text-[#C084FC]">{playerName}</p><h2 className="mt-1 font-display text-4xl uppercase leading-none text-white">Salón de medallas</h2></div>
              <button type="button" onClick={() => setIsOpen(false)} className="border border-white/20 bg-white/5 px-3 py-2 text-xs font-black uppercase text-white">Cerrar</button>
            </div>
            <div className="mt-4 flex items-center gap-3 border border-[#F7E04B]/40 bg-[#14141d] p-3">
              <img src={hiddenPointIcon} alt="" className="h-9 w-9 shrink-0 object-contain" />
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-wider text-[#F7E04B]">Puntos ocultos {displayHiddenFound}/{HIDDEN_POINTS_TOTAL}</p>
                <p className="mt-1 text-[11px] leading-4 text-white/70">Los puntos ocultos se regeneran cada hora.</p>
                {(countdownLabel || displayHiddenResetMs > 0) && (
                  <p className="mt-1 text-[11px] font-black uppercase tabular-nums tracking-wider text-[#C9A8FF]">
                    {displayHiddenFound >= HIDDEN_POINTS_TOTAL
                      ? `¡Los encontraste todos! Se regeneran en ${countdownLabel || `${hiddenResetMinutes} min`}`
                      : `Se regeneran en ${countdownLabel || `${hiddenResetMinutes} min`}`}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-6 grid gap-5">
              {groupedAchievements.map((section) => (
                <section key={section.group} aria-label={section.title}>
                  <p className="text-[10px] font-black uppercase tracking-[.22em] text-white/60">{section.title}</p>
                  <div className="mt-2 grid gap-2">
                    {section.items.map((achievement) => (
                      <div key={achievement.id} className={`flex items-center gap-3 border p-3 ${achievement.unlocked ? 'border-white/15 bg-[#1A1A1A]' : 'border-white/10 bg-white/[0.04] opacity-70'}`}>
                        <span title={achievement.title} className={`grid h-10 w-10 shrink-0 place-items-center rounded-full border text-xl ${achievement.unlocked ? 'border-[#C084FC] bg-[#6D28D9] text-white' : 'border-white/15 bg-[#111] text-white/40 grayscale'}`}>{achievement.icon}</span>
                        <div className="min-w-0 flex-1"><p className="text-xs font-black uppercase tracking-wider text-[#C084FC]">{achievement.title}</p><p className="mt-1 text-[11px] leading-4 text-[#C5E6A0]">{achievement.description}</p></div>
                        <span className="text-right text-[10px] font-black uppercase tracking-wider text-white">{achievement.unlocked ? 'Listo' : formatProgress(achievement)}</span>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
